import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BugReportIcon from '@mui/icons-material/BugReport';
import CoronavirusIcon from '@mui/icons-material/Coronavirus';
import { useTranslation } from 'react-i18next';
import apiService from '../services/api';

interface DiseasePest {
  id: number;
  name_ar: string;
  name_en: string;
  type: 'disease' | 'pest';
  created_at: string;
  updated_at: string;
}

const DiseasePestsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  
  const [items, setItems] = useState<DiseasePest[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<DiseasePest | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  
  const [formData, setFormData] = useState({
    name_ar: '',
    name_en: '',
    type: 'disease' as 'disease' | 'pest',
  });
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  // جلب البيانات
  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await apiService.getDiseasePests();
      setItems(response);
    } catch (error) {
      console.error('Error fetching disease pests:', error);
      showSnackbar(t('errorFetchingData'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // فتح Dialog للإضافة
  const handleAdd = () => {
    setEditingItem(null);
    setFormData({
      name_ar: '',
      name_en: '',
      type: 'disease',
    });
    setDialogOpen(true);
  };

  // فتح Dialog للتعديل
  const handleEdit = (item: DiseasePest) => {
    setEditingItem(item);
    setFormData({
      name_ar: item.name_ar,
      name_en: item.name_en,
      type: item.type,
    });
    setDialogOpen(true);
  };

  // حفظ (إضافة أو تعديل)
  const handleSave = async () => {
    // التحقق من صحة البيانات
    if (!formData.name_ar.trim() || !formData.name_en.trim()) {
      showSnackbar(t('pleaseFieldRequired'), 'error');
      return;
    }

    try {
      if (editingItem) {
        // تعديل
        await apiService.updateDiseasePest(editingItem.id, formData);
        showSnackbar(t('updatedSuccessfully'), 'success');
      } else {
        // إضافة
        await apiService.createDiseasePest(formData);
        showSnackbar(t('addedSuccessfully'), 'success');
      }
      
      setDialogOpen(false);
      fetchItems();
    } catch (error) {
      console.error('Error saving:', error);
      showSnackbar(t('errorSaving'), 'error');
    }
  };

  // فتح dialog الحذف
  const handleDeleteClick = (id: number) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  };

  // تأكيد الحذف
  const handleDeleteConfirm = async () => {
    if (deleteId === null) return;

    try {
      await apiService.deleteDiseasePest(deleteId);
      showSnackbar(t('deletedSuccessfully'), 'success');
      setDeleteDialogOpen(false);
      fetchItems();
    } catch (error) {
      console.error('Error deleting:', error);
      showSnackbar(t('errorDeleting'), 'error');
    }
  };

  const getTypeIcon = (type: string) => {
    return type === 'disease' ? <CoronavirusIcon /> : <BugReportIcon />;
  };

  const getTypeColor = (type: string) => {
    return type === 'disease' ? 'error' : 'warning';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* العنوان وزر الإضافة */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3 
      }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          {t('diseasesAndPests')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          sx={{
            bgcolor: '#10b981',
            '&:hover': { bgcolor: '#059669' },
          }}
        >
          {t('addNew')}
        </Button>
      </Box>

      {/* الجدول */}
      <TableContainer component={Paper} elevation={2}>
        <Table>
          <TableHead sx={{ bgcolor: '#f8fafc' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', textAlign: isRTL ? 'right' : 'left' }}>
                {t('type')}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', textAlign: isRTL ? 'right' : 'left' }}>
                {t('nameArabic')}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', textAlign: isRTL ? 'right' : 'left' }}>
                {t('nameEnglish')}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                {t('actions')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {t('noDataAvailable')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                    <Chip
                      icon={getTypeIcon(item.type)}
                      label={t(item.type)}
                      color={getTypeColor(item.type)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                    {item.name_ar}
                  </TableCell>
                  <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                    {item.name_en}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <IconButton
                      color="primary"
                      size="small"
                      onClick={() => handleEdit(item)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => handleDeleteClick(item.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog الإضافة/التعديل */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <DialogTitle>
          {editingItem ? t('edit') : t('addNew')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {/* النوع */}
            <FormControl fullWidth>
              <InputLabel>{t('type')}</InputLabel>
              <Select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as 'disease' | 'pest' })}
                label={t('type')}
              >
                <MenuItem value="disease">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CoronavirusIcon fontSize="small" />
                    {t('disease')}
                  </Box>
                </MenuItem>
                <MenuItem value="pest">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BugReportIcon fontSize="small" />
                    {t('pest')}
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            {/* الاسم بالعربية */}
            <TextField
              fullWidth
              label={t('nameArabic')}
              value={formData.name_ar}
              onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
              required
              dir="rtl"
            />

            {/* الاسم بالإنجليزية */}
            <TextField
              fullWidth
              label={t('nameEnglish')}
              value={formData.name_en}
              onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
              required
              dir="ltr"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>
            {t('cancel')}
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSave}
            sx={{
              bgcolor: '#10b981',
              '&:hover': { bgcolor: '#059669' },
            }}
          >
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog تأكيد الحذف */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <DialogTitle>{t('confirmDelete')}</DialogTitle>
        <DialogContent>
          <Typography>{t('confirmDeleteMessage')}</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {t('cancel')}
          </Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleDeleteConfirm}
          >
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar للإشعارات */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: isRTL ? 'left' : 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DiseasePestsPage;
